import React, { useEffect, useState, useMemo, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Calculator, FileText, Image, ShieldCheck, Search, Mail } from 'lucide-react'
import HomeTemplate from '../templates/HomeTemplate'
import ToolCard from '../organisms/ToolCard'
import './Home.css'

// Quick-access links to specialized/niche tools that deserve homepage visibility
// for internal linking and long-tail keyword targeting.
const POPULAR_SPECIALIZED_TOOLS = [
  {
    name: 'Pet Age Calculator',
    path: '/pet-age-calculator/',
    description: 'Convert your dog or cat\'s age into human years using breed-adjusted formulas'
  },
  {
    name: 'Islamic (Hijri) Age Calculator',
    path: '/islamic-age-calculator/',
    description: 'Calculate your age using the Islamic lunar calendar instead of the Gregorian one'
  },
  {
    name: 'Retirement Age Calculator',
    path: '/retirement-age-calculator/',
    description: 'Check your official retirement age and years remaining until then'
  },
  {
    name: 'Generation Calculator',
    path: '/generation-calculator/',
    description: 'Find out if you\'re Gen Z, Millennial, Gen X, or a Baby Boomer'
  },
  {
    name: 'Chronological Age Calculator',
    path: '/chronological-age-calculator/',
    description: 'Precise year-month-day age breakdown used for school and legal eligibility'
  }
];

// Tool categories for topical grouping / silo structure
const TOOL_CATEGORIES = [
  {
    name: 'Calculators',
    description: 'Age, percentage, and eligibility calculations',
    icon: Calculator,
    tools: [
      { name: 'Age Calculator', path: '/age/' },
      { name: 'Percentage Calculator', path: '/percentage/' },
      { name: 'Eligibility Calculator', path: '/eligibility-calculator/' }
    ]
  },
  {
    name: 'Text & Writing Tools',
    description: 'Analyze, format, and convert written content',
    icon: FileText,
    tools: [
      { name: 'Word Counter', path: '/word-counter/' },
      { name: 'Text to Speech', path: '/text-to-speech/' },
      { name: 'JSON Formatter', path: '/json-formatter/' }
    ]
  },
  {
    name: 'Image & Media Tools',
    description: 'Compress, convert, and extract media files',
    icon: Image,
    tools: [
      { name: 'Image Compressor', path: '/image-compressor/' },
      { name: 'Image Converter', path: '/image-converter/' },
      { name: 'YouTube Thumbnail Downloader', path: '/yt-thumbnail/' }
    ]
  },
  {
    name: 'Security & Utility Tools',
    description: 'Generate passwords and QR codes',
    icon: ShieldCheck,
    tools: [
      { name: 'Password Generator', path: '/password/' },
      { name: 'QR Code Generator', path: '/qr-generator/' }
    ]
  }
];

const FAQ_ITEMS = [
  {
    question: 'Is EveryKits really free to use?',
    answer: 'Yes. Every tool on EveryKits is completely free, with no hidden fees, subscriptions, or usage limits.'
  },
  {
    question: 'Do I need to create an account to use the tools?',
    answer: 'No account or signup is required. Every tool works instantly the moment you open it.'
  },
  {
    question: 'Is my data uploaded to a server when I use these tools?',
    answer: 'Most EveryKits tools, including the Word Counter, Password Generator, and Age Calculator, process everything locally in your browser. Your text, images, and files never leave your device.'
  },
  {
    question: 'Which age-related calculators does EveryKits offer?',
    answer: 'Beyond the standard Age Calculator, EveryKits includes calculators for pet age, Islamic (Hijri) age, retirement eligibility, generational cohorts, and precise chronological age.'
  },
  {
    question: 'Can I download multiple YouTube thumbnails at once?',
    answer: 'Yes. The YouTube Thumbnail Downloader supports bulk downloads as a ZIP file, alongside single-image downloads in multiple resolutions.'
  },
  {
    question: 'Can I use EveryKits tools on my mobile phone?',
    answer: 'Yes. Every tool is built mobile-first and works smoothly on smartphones, tablets, and desktop browsers alike.'
  },
  {
    question: 'What does the EveryKits newsletter send?',
    answer: 'Occasional emails about new tools, major updates, and blog guides — no spam, and you can unsubscribe anytime.'
  }
];

const Home = ({ tools = [] }) => {

  const [lastUpdated, setLastUpdated] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const searchInputRef = useRef(null);

  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle'); // idle | loading | success | error
  const [newsletterMessage, setNewsletterMessage] = useState('');

  const filteredTools = useMemo(() => {
    if (!searchQuery.trim()) return tools;
    const query = searchQuery.trim().toLowerCase();
    return tools.filter(
      (tool) =>
        tool.name.toLowerCase().includes(query) ||
        tool.description.toLowerCase().includes(query)
    );
  }, [tools, searchQuery]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim()) {
      setSearchParams({ q: value }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;

    setNewsletterStatus('loading');
    setNewsletterMessage('');

    try {
      const response = await fetch('/.netlify/functions/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail.trim() })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setNewsletterStatus('success');
        setNewsletterMessage('You\'re subscribed! Check your inbox for a confirmation.');
        setNewsletterEmail('');
      } else {
        setNewsletterStatus('error');
        setNewsletterMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setNewsletterStatus('error');
      setNewsletterMessage('Network error. Please try again in a moment.');
    }
  };

  // Ctrl+K / Cmd+K focuses the search box — the same shortcut used by
  // VS Code, GitHub, Notion, and other tool-heavy sites.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Computed only after mount so react-snap's prerendered HTML (built at a
  // fixed point in time) never disagrees with the client's Date() value —
  // that mismatch is what throws hydration Error #418.
  useEffect(() => {
    setLastUpdated(
      new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    );
  }, []);



  return (
    <HomeTemplate path="/" tools={tools}>
      <section className="hero-section">
        <h1>Free Online Tools for Everyone</h1>
        <p>EveryKits is a free, browser-based platform built around one idea: pick a task, run the tool, get your result — no signup, no installation, no cost. It includes an age calculator (with 8 specialized variants), a word counter with keyword density analysis, a bulk YouTube thumbnail downloader, a password generator, and more.</p>
        <p>Every tool processes your input directly in the browser wherever possible, so your text and files stay on your device instead of being uploaded to a server.</p>
      </section>

      <div className="tool-search-wrapper">
        <Search size={18} className="tool-search-icon" />
        <input
          ref={searchInputRef}
          type="text"
          className="tool-search-input"
          placeholder="Search tools — e.g. age, password, QR code..."
          value={searchQuery}
          onChange={handleSearchChange}
          aria-label="Search EveryKits tools"
        />
        <kbd className="tool-search-kbd" aria-hidden="true">Ctrl K</kbd>
      </div>

      <h2 className="tool-grid-heading" id="tools">
        {searchQuery ? `Results for "${searchQuery}"` : 'Explore Our Free Tools'}
      </h2>
      <div className="tool-grid">
        {filteredTools.length > 0 ? (
          filteredTools.map((tool, index) => (
            <ToolCard
              key={tool.id}
              icon={tool.icon}
              title={tool.name}
              description={tool.description}
              path={tool.path}
              className={`stagger-${index + 1}`}
            />
          ))
        ) : (
          <p className="no-tools-found">No tools match "{searchQuery}". Try a different keyword.</p>
        )}
      </div>

      <section className="categories-section">
        <h2>Browse Tools by Category</h2>
        <p>Tools grouped by what they're built for, so you can jump straight to the type of task you're solving.</p>
        <div className="categories-grid">
          {TOOL_CATEGORIES.map((category) => {
            const CategoryIcon = category.icon;
            return (
              <div className="category-card" key={category.name}>
                <div className="category-icon">
                  <CategoryIcon size={26} />
                </div>
                <h3>{category.name}</h3>
                <p className="category-description">{category.description}</p>
                <ul className="category-links">
                  {category.tools.map((tool) => (
                    <li key={tool.path}>
                      <Link to={tool.path}>{tool.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      <section className="how-it-works-section">
        <h2>How EveryKits Works</h2>
        <div className="how-it-works-steps">
          <div className="how-step">
            <div className="how-step-number">1</div>
            <h3>Pick a Tool</h3>
            <p>Choose from the tool grid above or a category — every tool has its own dedicated page.</p>
          </div>
          <div className="how-arrow" aria-hidden="true">
            <svg width="40" height="20" viewBox="0 0 40 20" fill="none">
              <path d="M0 10H36M36 10L28 2M36 10L28 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="how-step">
            <div className="how-step-number">2</div>
            <h3>Enter Your Input</h3>
            <p>Type text, paste a link, or upload a file. No account creation or setup step in between.</p>
          </div>
          <div className="how-arrow" aria-hidden="true">
            <svg width="40" height="20" viewBox="0 0 40 20" fill="none">
              <path d="M0 10H36M36 10L28 2M36 10L28 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="how-step">
            <div className="how-step-number">3</div>
            <h3>Get Your Result Instantly</h3>
            <p>Most tools calculate or process directly in your browser, so results appear immediately.</p>
          </div>
        </div>
      </section>

      <section className="specialized-tools-section">
        <h2>Specialized Calculators Worth Knowing About</h2>
        <p>Beyond the core Age Calculator, EveryKits includes a dedicated suite of calculators built for specific, real-world questions — from pet age conversion to Hijri calendar age.</p>
        <div className="specialized-tools-grid">
          {POPULAR_SPECIALIZED_TOOLS.map((tool) => (
            <Link key={tool.path} to={tool.path} className="specialized-tool-card">
              <h3>{tool.name}</h3>
              <p>{tool.description}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="trust-section">
        <h2>Built to Be Transparent</h2>
        <p>EveryKits is built and maintained by TrainDevs. You can read more about how the tools work, how your data is handled, and how to reach us if something's wrong.</p>
        <div className="trust-links">
          <Link to="/about/" className="trust-link">About Us</Link>
          <Link to="/privacy-policy/" className="trust-link">Privacy Policy</Link>
          <Link to="/terms-of-service/" className="trust-link">Terms of Service</Link>
          <Link to="/contact/" className="trust-link">Contact</Link>
        </div>
      </section>

      <section className="blog-teaser-section">
        <h2>Guides & Tutorials</h2>
        <p>Looking for tips on getting more out of a specific tool, or the reasoning behind a calculation? The EveryKits blog covers practical guides alongside the tools themselves.</p>
        <div className="blog-teaser-grid">
          <Link to="/blog/how-image-compression-works-behind-scenes/" className="blog-teaser-card">
            <h3>How Image Compression Works Behind the Scenes</h3>
            <p>A technical deep dive into the algorithms that shrink your images.</p>
          </Link>
          <Link to="/blog/best-free-online-tools-for-bloggers-2026/" className="blog-teaser-card">
            <h3>Best Free Online Tools for Bloggers in 2026</h3>
            <p>A complete free blogging stack — writing, image, SEO, and promotion tools.</p>
          </Link>
          <Link to="/blog/digital-detox-benefits-online-tools-2026/" className="blog-teaser-card">
            <h3>Digital Detox Benefits and Online Tools for 2026</h3>
            <p>Balancing technology use with healthy digital habits in the modern world.</p>
          </Link>
        </div>
        <Link to="/blog/" className="blog-teaser-link">View All Posts</Link>
      </section>

      <section className="newsletter-section">
        <div className="newsletter-icon">
          <Mail size={26} />
        </div>
        <h2>Get Notified About New Tools</h2>
        <p>Occasional emails when a new tool launches or a guide goes up. No spam, unsubscribe anytime.</p>
        <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
          <input
            type="email"
            className="newsletter-input"
            placeholder="you@example.com"
            value={newsletterEmail}
            onChange={(e) => setNewsletterEmail(e.target.value)}
            required
            disabled={newsletterStatus === 'loading'}
            aria-label="Email address for newsletter"
          />
          <button
            type="submit"
            className="newsletter-submit"
            disabled={newsletterStatus === 'loading'}
          >
            {newsletterStatus === 'loading' ? 'Subscribing...' : 'Subscribe'}
          </button>
        </form>
        {newsletterStatus === 'success' && (
          <p className="newsletter-message newsletter-message-success">{newsletterMessage}</p>
        )}
        {newsletterStatus === 'error' && (
          <p className="newsletter-message newsletter-message-error">{newsletterMessage}</p>
        )}
      </section>

      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="home-faq-list">
          {FAQ_ITEMS.map((item) => (
            <div className="home-faq-item" key={item.question}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="last-updated">
        <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>
      </div>
    </HomeTemplate>
  )
}

export default Home














